import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Fragment, useEffect, useMemo, useState } from "react";
import Header from "../../Header";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import { calcVolumes, defaultThresholds, thresholdDifferences } from "./calcVolumes";
import VolumesAPI from "../../../API/VolumesAPI";
import "./TotalVolume.css";

const MAX_LINES = 20;
const newLine = () => ({ key: crypto.randomUUID(), rowId: null, boxes: null });

const money = (v) => (v === null || v === undefined ? "" : `R ${Math.round(v).toLocaleString("en-ZA").replace(/,/g, " ")}`);
const price = (v) => (v === null || v === undefined ? "" : `R ${v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dose = (v) => (v === null || v === undefined ? "" : v.toLocaleString("en-ZA", { maximumFractionDigits: 3 }));

const TIER_LABEL = { 0: "Geen vlak", 1: "Vlak 1", 2: "Vlak 2", 3: "Vlak 3" };

const TotalVolume = (props) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hectares, setHectares] = useState(100);
  const [lines, setLines] = useState(() => Array.from({ length: 5 }, newLine));
  const [thresholds, setThresholds] = useState({ tier1MinRpha: 0, tier2MinRpha: 0, tier3MinInvoice: 0 });
  const [notice, setNotice] = useState("");
  const soon = (text) => {
    setNotice(text);
    setTimeout(() => setNotice(""), 4000);
  };

  // Reload when the crop selector changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    VolumesAPI.getVolumePricing(props.cropId)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setThresholds(defaultThresholds(data)); // first row = starting values
        setLines(Array.from({ length: 5 }, newLine));
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [props.cropId]);

  const differing = useMemo(() => thresholdDifferences(rows), [rows]);

  const result = useMemo(
    () => calcVolumes(rows, lines.filter((l) => l.rowId).map((l) => ({ ...l })), hectares, thresholds),
    [rows, lines, hectares, thresholds]
  );
  const resultByKey = useMemo(() => {
    const map = new Map();
    lines.filter((l) => l.rowId).forEach((l, i) => map.set(l.key, result.lines[i]));
    return map;
  }, [lines, result]);

  // A product on two lines (two pack sizes) gets a subtotal row under its last line
  const groupAfterKey = useMemo(() => {
    const filled = lines.filter((l) => l.rowId);
    const map = new Map();
    (result.groups || []).forEach((g) => map.set(filled[Math.max(...g.indexes)].key, g));
    return map;
  }, [lines, result]);

  const updateLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : [newLine()]));

  const productOptions = (line) =>
    rows
      .filter((r) => r.id === line.rowId || !lines.some((l) => l.rowId === r.id))
      .map((r) => ({ label: r.product_pack_name || r.product_name, value: r.id }));

  const setThreshold = (field) => (e) => setThresholds((t) => ({ ...t, [field]: e.value ?? 0 }));

  if (loading) return <LoadingSpinner text="Laai pryse..." />;

  const { totals, currentTier, nextTier } = result;

  return (
    <div className="total-volume-view">
      <Header title="TOTALE VOLUME" backPath="pastOrders" setPath={props.setPath} />
      <main className="total-volume-content container tv-page">
      {error && <Message severity="error" text={error} className="tv-message" />}

      {differing.length > 0 && (
        <Message
          severity="warn"
          className="tv-message"
          text={`Nie al die produkte vir hierdie gewas het dieselfde vlak-minimums nie (bv. ${differing[0].product_name}). Die eerste produk se waardes word gebruik. Jy kan dit hieronder verander.`}
        />
      )}

      <div className="tv-top">
        <div className="tv-card">
          <label htmlFor="tv-ha">Hektaar</label>
          <InputNumber inputId="tv-ha" value={hectares} onValueChange={(e) => setHectares(e.value ?? 0)} min={0} suffix=" ha" />
        </div>

        <div className="tv-card">
          <div className="tv-card-title">MyPack vlakke (minimum)</div>
          <div className="tv-threshold">
            <span>Vlak 1 lys R/ha</span>
            <InputNumber value={thresholds.tier1MinRpha} onValueChange={setThreshold("tier1MinRpha")} min={0} />
          </div>
          <div className="tv-threshold">
            <span>Vlak 2 lys R/ha</span>
            <InputNumber value={thresholds.tier2MinRpha} onValueChange={setThreshold("tier2MinRpha")} min={0} />
          </div>
          <div className="tv-threshold">
            <span>Vlak 3 totale lys faktuur</span>
            <InputNumber value={thresholds.tier3MinInvoice} onValueChange={setThreshold("tier3MinInvoice")} min={0} />
          </div>
        </div>

        <div className="tv-card tv-summary">
          <div>
            <span>Lys R/ha</span>
            <strong>{money(totals.listRpha)}</strong>
          </div>
          <div>
            <span>Totale lys faktuur</span>
            <strong>{money(totals.totalList)}</strong>
          </div>
          <div>
            <span>Huidige vlak</span>
            <strong className={`tv-tier tv-tier-${currentTier}`}>{TIER_LABEL[currentTier]}</strong>
          </div>
          <div>
            <span>Volgende vlak</span>
            <strong>{nextTier ? `${money(nextTier.moreNeeded)} meer vir Vlak ${nextTier.tier}` : "Hoogste vlak bereik"}</strong>
          </div>
        </div>
      </div>

      <div className="tv-table-wrap">
        <table className="tv-table">
          <thead>
            <tr>
              <th>Produk</th>
              <th>Bokse</th>
              <th>Volume (L/kg)</th>
              <th>Vol/ha</th>
              <th>Min vol/ha</th>
              <th>Maks vol/ha</th>
              <th>Toets</th>
              <th>Lys prys (R/L)</th>
              <th>MyPack prys (R/L)</th>
              <th>Lys waarde</th>
              <th>MyPack waarde</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const r = resultByKey.get(line.key);
              const row = rows.find((x) => x.id === line.rowId);
              const grp = groupAfterKey.get(line.key);
              return (
                <Fragment key={line.key}>
                <tr>
                  <td className="tv-product">
                    <Dropdown
                      value={line.rowId}
                      options={productOptions(line)}
                      onChange={(e) => updateLine(line.key, { rowId: e.value })}
                      placeholder="Kies produk"
                      filter
                      showClear
                    />
                  </td>
                  <td>
                    <InputNumber value={line.boxes} onValueChange={(e) => updateLine(line.key, { boxes: e.value })} min={0} disabled={!line.rowId} />
                  </td>
                  <td className="num">{r && r.volume ? dose(r.volume) : ""}</td>
                  <td className="num">{r && r.volume ? dose(r.volPerHa) : ""}</td>
                  <td className="num">{r ? dose(r.minDosage) : ""}</td>
                  <td className="num">{r ? dose(r.maxDosage) : ""}</td>
                  <td>
                    {r && r.checks.length > 0 ? (
                      r.checks.map((c) => (
                        <span key={c} className={`tv-check tv-check-${c === "Duplicate" || c === "Over max" ? "bad" : "warn"}`}>
                          {c}
                        </span>
                      ))
                    ) : r && r.volume ? (
                      <span className="tv-check tv-check-ok">OK</span>
                    ) : null}
                  </td>
                  <td className="num">{row ? price(row.list_price_pv) : ""}</td>
                  <td className="num">
                    {r && r.volume ? price(r.unitPrice) : ""}
                    {r && r.seasonCapped ? <span title="Seisoenprys"> *</span> : null}
                  </td>
                  <td className="num">{r && r.volume ? money(r.listValue) : ""}</td>
                  <td className="num">{r && r.volume ? money(r.myPackValue) : ""}</td>
                  <td>
                    <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => removeLine(line.key)} aria-label="Verwyder" />
                  </td>
                </tr>
                {grp && (
                  <tr className="tv-subtotal">
                    <td>Totaal {grp.name}</td>
                    <td className="num">{grp.boxes || ""}</td>
                    <td className="num">{dose(grp.volume)}</td>
                    <td className="num">{dose(grp.volPerHa)}</td>
                    <td className="num">{dose(grp.minDosage)}</td>
                    <td className="num">{dose(grp.maxDosage)}</td>
                    <td>
                      {grp.checks.length > 0 ? (
                        grp.checks.map((c) => (
                          <span key={c} className={`tv-check tv-check-${c === "Over max" ? "bad" : "warn"}`}>
                            {c}
                          </span>
                        ))
                      ) : (
                        <span className="tv-check tv-check-ok">OK</span>
                      )}
                    </td>
                    <td />
                    <td />
                    <td className="num">{money(grp.listValue)}</td>
                    <td className="num">{money(grp.myPackValue)}</td>
                    <td />
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Totaal</td>
              <td className="num">{totals.totalBoxes || ""}</td>
              <td colSpan={7} />
              <td className="num">{money(totals.totalList)}</td>
              <td className="num">{money(totals.totalMyPack)}</td>
              <td />
            </tr>
            <tr>
              <td colSpan={9} className="num">
                Per ha
              </td>
              <td className="num">{money(totals.listRpha)}</td>
              <td className="num">{money(totals.myPackRpha)}</td>
              <td />
            </tr>
            <tr>
              <td colSpan={9} className="num">
                Besparing
              </td>
              <td colSpan={2} className="num">
                {money(totals.totalDiscount)} ({money(totals.listRpha - totals.myPackRpha)}/ha)
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="tv-actions">
        <Button
          label="Voeg produk by"
          icon="pi pi-plus"
          className="p-button-outlined"
          disabled={lines.length >= MAX_LINES}
          onClick={() => setLines((ls) => [...ls, newLine()])}
        />
        <Button label="Stoor beeld" icon="pi pi-image" className="p-button-outlined" onClick={() => soon("Om die beeld te stoor en af te laai kom binnekort.")} />
        <Button label="Heg spuit program aan" icon="pi pi-paperclip" onClick={() => soon("Heg spuit program aan kom binnekort.")} />
      </div>
      {notice && <Message severity="info" text={notice} className="tv-message" />}

      <p className="tv-note">
        Beraming. Die finale prys word op die bestelling bepaal (vennoot-produkte en weer/registrasie-toetse is nie hier ingereken nie). * = seisoenprys toegepas.
      </p>
      </main>
    </div>
  );
};

export default TotalVolume;
